'use client';

import { useEffect } from 'react';

import {
  syncAttributionEventProperties,
  withAttributionParams,
} from '@m/utils/reservationFunnelContext';

import { CUSTOMER_APP_URL } from '../constants/links';

function getCustomerAppOrigin(): string | undefined {
  try {
    return new URL(CUSTOMER_APP_URL).origin;
  } catch {
    return undefined;
  }
}

/**
 * Appends the session's captured UTMs to a clicked link when it points at the customer app,
 * including sign-in. The app stores those params at startup, before login redirects drop the
 * query string, so later funnel events still see the marketing attribution.
 */
export function decorateCustomerAppLink(event: Event, customerOrigin: string) {
  const anchor = (event.target as Element | null)?.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  try {
    const url = new URL(anchor.href);
    if (url.origin !== customerOrigin) return;
  } catch {
    return;
  }
  anchor.href = withAttributionParams(anchor.href);
}

/**
 * Captures first-touch attribution (UTMs, gclid, referrer) for the session, sets the Heap
 * `trafficSource` / `landingPage` / `utm_*` event props, and appends the captured UTMs to links
 * into the customer app so its funnel events keep the marketing-site attribution.
 *
 * The automatic Heap pageview is stamped earlier, from the inline snippet (`attributionEarlyScript`),
 * because this effect runs after a cached SDK may already have sent it. The effect still re-applies
 * the same props for later events.
 *
 * Links are decorated at click time rather than rendered with UTMs because these pages are
 * statically cached and shared across visitors.
 */
export function AttributionTracker() {
  useEffect(() => {
    syncAttributionEventProperties();

    const customerOrigin = getCustomerAppOrigin();
    if (!customerOrigin) return undefined;

    const onClick = (event: Event) => decorateCustomerAppLink(event, customerOrigin);

    // Capture phase so the href is updated before the browser (or a handler) navigates.
    // `auxclick` covers middle-click / open-in-new-tab, which `click` does not fire for.
    document.addEventListener('click', onClick, true);
    document.addEventListener('auxclick', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('auxclick', onClick, true);
    };
  }, []);

  return null;
}
